import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  LinearProgress,
  Skeleton,
  ThemeProvider,
  createTheme,
  List,
  ListItem,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import ArchiveIcon from "@mui/icons-material/Archive";
import { toast } from "react-toastify";
import { targetsApi } from "../../Components/targetsApi";
import { useTheme } from "../../hooks/useTheme";
import { createMuiThemeFromMode } from "../../theme/theme";
import { RoleContext } from "../../Contexts/RoleContext";
import styles from "../AllEnquiries.module.scss";

const baseThemeOptions = {
  palette: {
    secondary: { main: "#f5365c" },
    success: { main: "#2dce89" },
    warning: { main: "#fb6340" },
    info: { main: "#11cdef" },
  },
  typography: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    h4: { fontWeight: 600, letterSpacing: "-0.02em" },
    button: { textTransform: "none", fontWeight: 500 },
  },
  shape: { borderRadius: 12 },
};

function TargetProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { themeMode } = useTheme();
  const theme = useMemo(
    () => createTheme(createMuiThemeFromMode(themeMode, baseThemeOptions)),
    [themeMode]
  );
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { role } = React.useContext(RoleContext);
  const canManage = role === "admin" || role === "Manager";

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    targetsApi
      .getById(id)
      .then(setData)
      .catch((err) => {
        toast.error(err?.message || "Failed to load target");
        setData(null);
      })
      .finally(() => setLoading(false));
  }, [id]);

  const target = data?.target;
  const formatProgress = (t) => {
    if (!t) return "";
    if (t.metric === "conversion_rate") return `${Number(t.current_progress).toFixed(1)}%`;
    if (t.metric === "revenue") return Number(t.current_progress).toLocaleString();
    return String(Math.round(t.current_progress));
  };
  const formatGoal = (t) => {
    if (!t) return "";
    if (t.metric === "conversion_rate") return `${Number(t.goal).toFixed(0)}%`;
    if (t.metric === "revenue") return Number(t.goal).toLocaleString();
    return String(Math.round(t.goal));
  };

  const handleArchive = async () => {
    if (!target?.id) return;
    try {
      await targetsApi.archive(target.id);
      toast.success("Target archived.");
      navigate("/targets");
    } catch (err) {
      toast.error(err?.message || "Failed to archive");
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Box className={styles.root}>
        <Paper elevation={0} className={styles.contentPaper}>
              <Box sx={{ mb: 2 }}>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/targets")} sx={{ mb: 1 }}>
                  Back to Targets
                </Button>
                {canManage && !target?.archived && (
                  <Button startIcon={<EditIcon />} onClick={() => navigate(`/targets/${id}/edit`)} sx={{ ml: 1 }}>
                    Edit
                  </Button>
                )}
                {canManage && !target?.archived && (
                  <Button startIcon={<ArchiveIcon />} color="secondary" onClick={handleArchive} sx={{ ml: 1 }}>
                    Archive
                  </Button>
                )}
              </Box>

              {loading && (
                <Box>
                  <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2, mb: 2 }} />
                  <Skeleton variant="rectangular" height={150} sx={{ borderRadius: 2 }} />
                </Box>
              )}

              {!loading && !target && (
                <Typography color="text.secondary">Target not found.</Typography>
              )}

              {!loading && target && (
                <>
                  {/* 1. Target Overview */}
                  <Card sx={{ mb: 3, borderRadius: 2 }}>
                    <CardContent>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                        <Box sx={{ bgcolor: "primary.main", color: "primary.contrastText", p: 1.5, borderRadius: 2 }}>
                          <TrackChangesIcon fontSize="large" />
                        </Box>
                        <Box>
                          <Typography variant="h5" fontWeight={600}>{target.name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {target.metric_label} · {target.start_date} to {target.end_date}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Assigned: {(target.employees || []).map((e) => e.display_name).join(", ") || "—"}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Scope: {target.scope_display ?? (target.scope_type === "overall" ? "Overall" : "—")}
                          </Typography>
                        </Box>
                      </Box>
                      {target.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {target.description}
                        </Typography>
                      )}
                      <Typography variant="h6" gutterBottom>
                        Progress: {formatProgress(target)} / {formatGoal(target)}
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(100, target.percentage || 0)}
                        sx={{ borderRadius: 1, height: 12, mb: 2 }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        Reward:{" "}
                        {target.reward_type === "cash" && target.reward_value != null
                          ? `Cash ${Number(target.reward_value).toLocaleString()}`
                          : target.reward_description || target.reward_type}
                      </Typography>
                    </CardContent>
                  </Card>

                  {/* 2. Activity Timeline */}
                  <Typography variant="h6" sx={{ mb: 1 }}>Activity Timeline</Typography>
                  <Card sx={{ mb: 3, borderRadius: 2 }}>
                    <List dense>
                      {(data.timeline || []).length === 0 ? (
                        <ListItem>
                          <ListItemText primary="No activity yet." />
                        </ListItem>
                      ) : (
                        (data.timeline || []).map((item) => (
                          <ListItem key={item.id}>
                            <ListItemText
                              primary={`${item.employee_name} contributed ${item.metric_label?.toLowerCase()} → +${item.value}${target.metric === "conversion_rate" ? "%" : ""}`}
                              secondary={item.created_at}
                            />
                          </ListItem>
                        ))
                      )}
                    </List>
                  </Card>

                  {/* 3. Linked Conversions / Revenue */}
                  <Typography variant="h6" sx={{ mb: 1 }}>Linked Conversions / Revenue</Typography>
                  <Card sx={{ borderRadius: 2 }}>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Student</TableCell>
                            <TableCell>Program</TableCell>
                            <TableCell>Revenue</TableCell>
                            <TableCell>Date</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(data.linked_records || []).length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={4}>No linked records yet.</TableCell>
                            </TableRow>
                          ) : (
                            (data.linked_records || []).map((row, idx) => (
                              <TableRow key={idx}>
                                <TableCell>{row.student}</TableCell>
                                <TableCell>{row.program}</TableCell>
                                <TableCell>{typeof row.revenue === "number" ? row.revenue.toLocaleString() : row.revenue}</TableCell>
                                <TableCell>{row.date}</TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Card>
                </>
              )}
            </Paper>
      </Box>
    </ThemeProvider>
  );
}

export default TargetProfilePage;
